import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Add from "@mui/icons-material/Add";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import Launch from "@mui/icons-material/Launch";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import ChairOutlined from "@mui/icons-material/ChairOutlined";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import PhotoLibraryOutlined from "@mui/icons-material/PhotoLibraryOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import SensorsOutlined from "@mui/icons-material/SensorsOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import CloudDoneOutlined from "@mui/icons-material/CloudDoneOutlined";
import Star from "@mui/icons-material/Star";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmber from "@mui/icons-material/WarningAmber";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import DiamondOutlined from "@mui/icons-material/DiamondOutlined";
import ArchitectureOutlined from "@mui/icons-material/ArchitectureOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import VolunteerActivismOutlined from "@mui/icons-material/VolunteerActivismOutlined";
import axios from "axios";
import apiService from "../../../../service/Api";
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../../components/common/ShowToast";
import { AMENITY_OPTIONS, type VenueData } from "../../types/venues.types";
import WebsitePreview, { type PageSection, type TemplateId, TEMPLATES } from "../../../configurations/venue-website-visibility/WebsitePreview";
import VenuePublishedModal from "./VenuePublishedModal";
import {
  DEFAULT_FIELDS,
  DEFAULT_SECTIONS,
  unlockList,
  type VisibilityField,
} from "../../websiteVisibility";

const isKnownTemplate = (val: unknown): val is TemplateId =>
  typeof val === "string" && (val === "modern" || val === "luxury" || val === "vibrant" || val === "studio");

/* =========================================================
   TYPES
   ========================================================= */

type PublicLayout = {
  id: number;
  layout_type: string;
  capacity: number | string;
};

type PublicationAction = "publish" | "unpublish";

/** Edits save on their own this long after the last change. */
const AUTOSAVE_DELAY_MS = 1200;
/** Checks the server would turn a save down for; a live listing can't lose these either. */
const SAVE_BLOCKERS = new Set(["name", "coordinates", "layout-rows"]);
const LIVE_SAVE_BLOCKERS = new Set(["city", "description", "capacity"]);

type UnknownRecord = Record<string, unknown>;

export interface Publication {
  is_published: boolean;
  can_publish: boolean;
  slug: string;
  public_url: string | null;
  website_url?: string | null;
  published_at: string | null;
  seo_active?: boolean;
  seo_metadata?: Record<string, unknown>;

  // Values from the venue record; the details below can be edited in the panel.
  name: string;
  address: string;
  city: string;
  suburb: string;
  country: string;
  venue_type: string;
  capacity: number | null;
  latitude: number | null;
  longitude: number | null;
  description: string;
  amenities: string[];
  layouts: PublicLayout[];
  contact_phone: string;
  contact_email: string;

  // The cover is the one thing chosen on the listing itself.
  cover_photo_id: number | null;
  template?: TemplateId;
  chema_url?: string;

  // Who sees each detail on the website (set for every venue), and the page's section order.
  visibility?: VisibilityField[];
  sections?: PageSection[];
  section_order?: string[];
  rooms?: { name: string; capacity: number | null }[];
  contact_name?: string;

  // For editing in the panel.
  street_address?: string;
  venue_type_options?: { value: string; label: string }[];
  photos?: { id: number; url: string }[];
  layout_type_options?: { value: string; label: string }[];
  contacts?: PublicContact[];
  display_contact_id?: number | null;
  extra_fields?: ExtraField[];
}

/** An extra detail Admins added in the website settings, as the panel edits it (backend: editable_extra_fields). */
type ExtraField = {
  key: string;
  label: string;
  group: string;
  input: "text" | "longtext" | "yesno" | "number" | "decimal" | "country" | "timezone" | "files" | string;
  value: unknown;
  choices?: { value: string; label: string }[];
};

/** A contact person as the panel lists them. */
type PublicContact = { id: number; name: string; phone: string; email: string };

/** The details being edited, as typed (numbers stay text until saved). */
type Draft = {
  name: string;
  venue_type: string;
  capacity: string;
  city: string;
  suburb: string;
  street_address: string;
  description: string;
  amenities: string[];
  latitude: string;
  longitude: string;
  /** Seating layouts as typed; replaces the venue's when saved. */
  layouts: { layout_type: string; capacity: string }[];
  /** The contact the website shows (null: the first one linked). */
  contact_id: number | null;
  /** Chema fundraiser embed URL or snippet */
  chema_url: string;
  /** Extra details from the website settings, by key: text as typed, or yes/no. */
  extras: Record<string, string | boolean>;
};

/** One thing the listing needs (or would benefit from), in plain words. */
type Check = {
  key: string;
  /** What to do, as a short sentence. */
  todo: string;
  passed: boolean;
  /** Required checks block publishing; the rest only improve the listing. */
  required: boolean;
  /** Required checks the website also re-applies to live listings: failing one hides a live venue. */
  hidesLive?: boolean;
  /** Where to go to fix it: a field in this panel, or the venue's own edit page. */
  target: keyof Draft | "cover" | "photos" | "edit-venue";
};

interface VenuePublicationProps {
  venueData: VenueData;
  onEditVenue?: () => void;
}

/* =========================================================
   CONSTANTS
   ========================================================= */

// Venue types the website refuses to list (mirrors the backend's PUBLIC_TYPES).
const UNSUPPORTED_PUBLIC_TYPES = new Set(["private_residence", "family_home", "blank_one", "blank_two"]);
const RECOMMENDED_PHOTOS = 3;
const DESCRIPTION_LIMIT = 5000;
const PHOTO_PATTERN = /\.(jpe?g|png|webp|gif|avif)(?:[?#]|$)/i;

const FIELD_LABELS: Record<string, string> = {
  cover_photo_id: "Cover photo",
  street_address: "Street address",
  venue_type: "Venue type",
  photos: "Photos",
};

/* =========================================================
   HELPERS
   ========================================================= */

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : {};
}

function formatLabel(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isTrueLike(value: unknown) {
  if (value === true) return true;
  return typeof value === "string" && ["yes", "true", "1"].includes(value.trim().toLowerCase());
}

function formatDate(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function isWebLink(value: string | null): value is string {
  return Boolean(value && /^https?:\/\//i.test(value));
}

const DEFAULT_WEBSITE_BASE =
  process.env.REACT_APP_MUZUKURU_WEBSITE_URL ||
  (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:3000"
    : "https://muzukuru.com");

function resolvePublicUrl(url: string | null | undefined, slug?: string | null): string | null {
  if (isWebLink(url ?? null)) return url!;
  if (slug) return `${DEFAULT_WEBSITE_BASE.replace(/\/+$/, "")}/venues/${slug}`;
  return null;
}

function getErrorMessage(error: unknown, fallback = "Could not update the website listing. Please try again."): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const describe = (value: unknown): string => {
      if (Array.isArray(value)) return value.map(describe).join(" ");
      if (value && typeof value === "object") {
        return Object.entries(value)
          .map(([key, item]) => (key === "detail" ? describe(item) : `${FIELD_LABELS[key] ?? formatLabel(key)}: ${describe(item)}`))
          .join("\n");
      }
      return String(value);
    };
    return describe(error.response.data);
  }
  return fallback;
}

const draftFrom = (publication: Publication): Draft => ({
  name: publication.name ?? "",
  venue_type: publication.venue_type ?? "",
  capacity: publication.capacity != null ? String(publication.capacity) : "",
  city: publication.city ?? "",
  suburb: publication.suburb ?? "",
  street_address: publication.street_address ?? publication.address ?? "",
  description: publication.description ?? "",
  amenities: publication.amenities ?? [],
  latitude: publication.latitude != null ? String(publication.latitude) : "",
  longitude: publication.longitude != null ? String(publication.longitude) : "",
  layouts: (publication.layouts ?? []).map((layout) => ({ layout_type: layout.layout_type ?? "", capacity: layout.capacity != null ? String(layout.capacity) : "" })),
  contact_id: publication.display_contact_id ?? null,
  chema_url: publication.chema_url ?? "",
  extras: Object.fromEntries(
    (publication.extra_fields ?? [])
      .filter((field) => field.input !== "files")
      .map((field) => [field.key, field.input === "yesno" ? Boolean(field.value) : field.value == null ? "" : String(field.value)]),
  ),
});

const numberOrNull = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : NaN;
};

/** Only what changed, in the shape the details endpoint takes. */
function changedDetails(saved: Draft, draft: Draft, extraFields: ExtraField[] = []): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  // Extra details go together, each in the type its input needs.
  const extras: Record<string, unknown> = {};
  extraFields.forEach((field) => {
    const before = saved.extras[field.key];
    const after = draft.extras[field.key];
    if (after === undefined || (typeof after === "string" ? after.trim() === String(before ?? "").trim() : after === before)) return;
    if (field.input === "number" || field.input === "decimal") extras[field.key] = String(after).trim() ? Number(after) : null;
    else extras[field.key] = typeof after === "string" ? after.trim() : after;
  });
  if (Object.keys(extras).length) changes.extras = extras;
  (Object.keys(draft) as (keyof Draft)[]).forEach((key) => {
    if (key === "extras") return;
    const before = saved[key];
    const after = draft[key];
    const same = typeof after === "string" ? after.trim() === String(before).trim() : JSON.stringify(after) === JSON.stringify(before);
    if (same) return;
    if (key === "layouts") changes[key] = draft.layouts.map((layout) => ({ layout_type: layout.layout_type, capacity: Number(layout.capacity) }));
    else if (key === "capacity") changes[key] = numberOrNull(after as string);
    else if (key === "latitude" || key === "longitude") changes[key] = (after as string).trim() || null;
    else changes[key] = after;
  });
  // Coordinates are saved as a pair.
  if ("latitude" in changes || "longitude" in changes) {
    changes.latitude = draft.latitude.trim() || null;
    changes.longitude = draft.longitude.trim() || null;
  }
  return changes;
}

/**
 * What the listing needs, from the details as they're being edited. The required
 * ones mirror the backend's publish rules (PublicationInputSerializer and
 * public_listings); the rest make a better listing without blocking it.
 */
function buildChecks(draft: Draft, country: string, coverId: number | null, photoCount: number, eligible: boolean, extras: { contact: boolean }): Check[] {
  const capacity = numberOrNull(draft.capacity);
  const latitude = numberOrNull(draft.latitude);
  const longitude = numberOrNull(draft.longitude);
  const coordinatesOk = (latitude === null) === (longitude === null) && !Number.isNaN(latitude) && !Number.isNaN(longitude);
  const layoutsOk = draft.layouts.every((layout) => {
    const seats = Number(layout.capacity);
    return Boolean(layout.layout_type) && Number.isInteger(seats) && seats >= 1 && seats <= 1_000_000;
  });
  return [
    { key: "name", todo: "Give the venue a name", passed: Boolean(draft.name.trim()), required: true, hidesLive: true, target: "name" },
    { key: "type", todo: "Choose a venue type the website lists", passed: Boolean(draft.venue_type) && !UNSUPPORTED_PUBLIC_TYPES.has(draft.venue_type), required: true, hidesLive: true, target: "venue_type" },
    { key: "capacity", todo: "Add how many guests it holds", passed: capacity !== null && Number.isInteger(capacity) && capacity >= 1 && capacity <= 1_000_000, required: true, hidesLive: true, target: "capacity" },
    { key: "city", todo: "Add the city", passed: Boolean(draft.city.trim()), required: true, hidesLive: true, target: "city" },
    { key: "country", todo: "Set the country in the venue's details", passed: Boolean(country.trim()), required: true, hidesLive: true, target: "edit-venue" },
    { key: "description", todo: "Write a description (AI can help)", passed: Boolean(draft.description.trim()), required: true, target: "description" },
    { key: "cover", todo: "Choose a cover photo", passed: coverId !== null, required: true, hidesLive: true, target: "cover" },
    { key: "eligible", todo: "Private homes and rooms inside a venue can't be listed. Publish the main venue instead", passed: eligible, required: true, hidesLive: true, target: "edit-venue" },
    { key: "coordinates", todo: "Enter both latitude and longitude, or neither", passed: coordinatesOk, required: true, target: "latitude" },
    { key: "layout-rows", todo: "Finish each layout (style and guests) or remove it", passed: layoutsOk, required: true, target: "layouts" },
    { key: "photos", todo: `Add at least ${RECOMMENDED_PHOTOS} photos`, passed: photoCount >= RECOMMENDED_PHOTOS, required: false, target: "photos" },
    { key: "amenities", todo: "Pick the amenities families will look for", passed: draft.amenities.length > 0, required: false, target: "amenities" },
    { key: "map", todo: "Add a map pin for exact directions", passed: latitude !== null && longitude !== null && coordinatesOk, required: false, target: "latitude" },
    { key: "layouts", todo: "Add the seating layouts", passed: draft.layouts.length > 0, required: false, target: "layouts" },
    { key: "contact", todo: "Choose a contact person for families", passed: extras.contact, required: false, target: "contact_id" },
  ];
}

/** The form's sections, in order, for the list on the left. */
const SECTIONS = [
  { key: "basics", label: "Basics", icon: <StorefrontOutlined /> },
  { key: "template", label: "Template", icon: <PaletteOutlined /> },
  { key: "description", label: "Description", icon: <NotesOutlined /> },
  { key: "amenities", label: "Amenities", icon: <ChecklistOutlined /> },
  { key: "photos", label: "Photos", icon: <PhotoLibraryOutlined /> },
  { key: "layouts", label: "Layouts", icon: <ChairOutlined /> },
  { key: "contact", label: "Contact", icon: <PersonOutline /> },
  { key: "chema", label: "Chema Fundraiser", icon: <VolunteerActivismOutlined /> },
] as const;
type SectionKey = (typeof SECTIONS)[number]["key"];

const SECTION_OF: Record<Check["target"], SectionKey | null> = {
  name: "basics", venue_type: "basics", capacity: "basics", city: "basics", suburb: "basics", street_address: "basics",
  latitude: "basics", longitude: "basics", description: "description", amenities: "amenities", cover: "photos",
  photos: "photos", layouts: "layouts", contact_id: "contact", chema_url: "chema", extras: null, "edit-venue": null,
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function VenuePublication({ venueData, onEditVenue }: VenuePublicationProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const endpoint = `/api/venues/venue/${venueData.id}/publication/`;

  const [publication, setPublication] = useState<Publication | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [coverId, setCoverId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [autosave, setAutosave] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [retry, setRetry] = useState(0);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [publishedModalOpen, setPublishedModalOpen] = useState(false);
  const [rating, setRating] = useState<{ average: number; count: number } | null>(null);
  const [showMapPin, setShowMapPin] = useState(false);
  const [newAmenity, setNewAmenity] = useState("");
  // Contacts found with "Link another contact", added to the choices until saved.
  const [foundContacts, setFoundContacts] = useState<PublicContact[]>([]);
  const [contactSearch, setContactSearch] = useState<{ query: string; loading: boolean; results: PublicContact[]; error: string } | null>(null);
  const [ai, setAi] = useState<{ mode: "write" | "improve"; loading: boolean; suggestions: string[]; error: string } | null>(null);
  const [todoAnchor, setTodoAnchor] = useState<HTMLElement | null>(null);
  const [addingAmenity, setAddingAmenity] = useState(false);
  const busy = useRef(false);
  // What failed to autosave, so the same changes aren't retried in a loop.
  const failedSave = useRef("");
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const photoInput = useRef<HTMLInputElement>(null);
  const planInput = useRef<HTMLInputElement>(null);

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>(() => {
    try {
      const stored = localStorage.getItem(`venue_${venueData.id}_template`);
      if (isKnownTemplate(stored)) {
        return stored;
      }
    } catch {
      // ignore
    }
    return "modern";
  });

  const handleSelectTemplate = (id: TemplateId) => {
    setSelectedTemplate(id);
    try {
      localStorage.setItem(`venue_${venueData.id}_template`, id);
    } catch {
      // ignore
    }
    showSuccessToast(`Applied "${TEMPLATES[id].name}" template`);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    apiService
      .get<Publication>(endpoint)
      .then(({ data }) => {
        if (!active) return;
        setPublication(data);
        if (isKnownTemplate(data.template)) {
          setSelectedTemplate(data.template);
        }
      })
      .catch(() => active && setLoadError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [endpoint, retry]);

  // The website rating, for staff who can see reviews. Purely informational:
  // anything other than a list of reviews (no permission, an error) shows nothing.
  useEffect(() => {
    let active = true;
    apiService
      .get<{ results?: { rating: number; status?: string }[] }>(`/api/venues/venue/${venueData.id}/reviews/`)
      .then(({ data }) => {
        const shown = Array.isArray(data?.results) ? data.results.filter((review) => review.status !== "rejected") : [];
        if (active && shown.length > 0) {
          const total = shown.reduce((sum, review) => sum + review.rating, 0);
          setRating({ average: total / shown.length, count: shown.length });
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [venueData.id]);

  // Public-eligible photos: from the listing (kept up to date as photos are added here), else from the venue.
  const photos = useMemo(() => {
    if (publication?.photos) return publication.photos.map((photo) => ({ id: photo.id, file: photo.url }));
    return (venueData.files ?? [])
      .filter((file) => ["venue_photos", "room_photos"].includes(file.file_type) && PHOTO_PATTERN.test(file.file))
      .map((file) => ({ id: file.id, file: file.file }));
  }, [publication?.photos, venueData.files]);

  const venueRecord = asRecord(venueData);
  const parentValue = venueRecord.parent_id ?? venueRecord.parent;
  const isChildVenue = parentValue !== undefined && parentValue !== null && parentValue !== false;

  // The saved cover, if its photo still exists (it may have been removed since).
  const savedCoverId =
    publication?.cover_photo_id != null && photos.some((photo) => photo.id === publication.cover_photo_id) ? publication.cover_photo_id : null;
  const saved = useMemo(() => (publication ? draftFrom(publication) : null), [publication]);
  const current = draft ?? saved;

  const eligibleFor = (venueType: string) =>
    !isTrueLike(venueRecord.is_private_residence) && venueType !== "private_residence" && venueType !== "family_home" && !isChildVenue;
  // The contacts to choose from, and the one the website will show (the first linked, unless one is chosen).
  const contactChoices = [...(publication?.contacts ?? []), ...foundContacts.filter((found) => !(publication?.contacts ?? []).some((linked) => linked.id === found.id))];
  const shownContact = contactChoices.find((person) => person.id === (draft ?? (publication ? draftFrom(publication) : null))?.contact_id) ?? contactChoices[0] ?? null;
  const extras = {
    contact: shownContact ? Boolean(shownContact.phone || shownContact.email) : Boolean(publication?.contact_phone?.trim() || publication?.contact_email?.trim()),
  };

  // The card always reflects what's saved; the panel reflects what's being edited.
  const savedChecks = publication && saved ? buildChecks(saved, publication.country, savedCoverId, photos.length, eligibleFor(saved.venue_type), extras) : [];
  const checks = publication && current ? buildChecks(current, publication.country, coverId, photos.length, eligibleFor(current.venue_type), extras) : [];
  const missing = checks.filter((check) => check.required && !check.passed);
  const niceToHave = checks.filter((check) => !check.required && !check.passed);
  const readyToPublish = missing.length === 0;

  const savedRequired = savedChecks.filter((check) => check.required);
  const savedMissing = savedRequired.filter((check) => !check.passed);
  const savedReadyCount = savedRequired.length - savedMissing.length;
  const hiddenWhileLive = Boolean(publication?.is_published) && savedChecks.some((check) => check.hidesLive && !check.passed);

  const status: "live" | "hidden" | "draft" | "none" = publication?.is_published
    ? hiddenWhileLive ? "hidden" : "live"
    : publication?.slug ? "draft" : "none";
  const statusLabel = { live: "Live", hidden: "Live · hidden", draft: "Draft", none: "Not listed" }[status];
  const statusColor = {
    live: theme.palette.success.main,
    hidden: theme.palette.warning.main,
    draft: theme.palette.warning.main,
    none: theme.palette.text.disabled,
  }[status];

  const selectedCover = photos.find((photo) => photo.id === coverId);
  const savedCover = photos.find((photo) => photo.id === savedCoverId);
  const coverChanged = coverId !== savedCoverId;
  const savedTemplate: TemplateId = (publication?.template as TemplateId) || "modern";
  const templateChanged = selectedTemplate !== savedTemplate;
  const extraFields = publication?.extra_fields ?? [];
  const detailChanges = saved && current ? changedDetails(saved, current, extraFields) : {};
  const hasChanges = coverChanged || templateChanged || Object.keys(detailChanges).length > 0;
  // Changes that can't be saved until fixed (the server would refuse them).
  const saveBlockers = checks.filter(
    (check) =>
      !check.passed &&
      (SAVE_BLOCKERS.has(check.key) ||
        (check.key === "capacity" && current?.capacity.trim() !== "") ||
        (Boolean(publication?.is_published) && LIVE_SAVE_BLOCKERS.has(check.key))),
  );
  const pendingKey = hasChanges
    ? JSON.stringify([detailChanges, coverChanged ? coverId : null, templateChanged ? selectedTemplate : null])
    : "";
  const publicUrl = resolvePublicUrl(publication?.public_url, publication?.slug);
  const visibilityFields = publication?.visibility?.length ? publication.visibility : DEFAULT_FIELDS;
  const typeOptions = publication?.venue_type_options?.length
    ? publication.venue_type_options
    : current?.venue_type ? [{ value: current.venue_type, label: formatLabel(current.venue_type) }] : [];
  const amenityChoices = Array.from(new Set([...AMENITY_OPTIONS, ...(current?.amenities ?? [])]));
  const missingKeys = new Set(missing.map((check) => check.target));

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((before) => ({ ...(before ?? saved!), [key]: value }));

  const handleOpen = () => {
    if (!publication) return;
    setDraft(draftFrom(publication));
    setFoundContacts([]);
    setContactSearch(null);
    setCoverId(savedCoverId);
    setShowMapPin(Boolean(publication.latitude != null || publication.longitude != null));
    setAi(null);
    setAutosave("idle");
    failedSave.current = "";
    if (isKnownTemplate(publication.template)) {
      setSelectedTemplate(publication.template);
    }
    setDialogOpen(true);
  };

  const closeNow = () => {
    setDialogOpen(false);
    setDiscardDialogOpen(false);
    setDraft(null);
    if (publication && isKnownTemplate(publication.template)) {
      setSelectedTemplate(publication.template);
    }
  };

  /** Saves what's been edited (details on the venue, the cover on the listing). True when nothing is left unsaved. */
  const saveNow = async (): Promise<boolean> => {
    if (!publication?.can_publish || !hasChanges) return true;
    if (busy.current || saveBlockers.length > 0) return false;
    busy.current = true;
    setAutosave("saving");
    const sent = draftRef.current;
    try {
      let data = publication;
      if (Object.keys(detailChanges).length > 0) data = (await apiService.patch<Publication>(`${endpoint}details/`, detailChanges)).data;
      if (coverChanged || templateChanged) {
        data = (await apiService.put<Publication>(endpoint, {
          action: "save",
          ...(coverChanged || coverId !== null ? { cover_photo_id: coverId } : {}),
          template: selectedTemplate,
          chema_url: draftRef.current?.chema_url ?? current?.chema_url ?? "",
        })).data;
      }
      setPublication(data);
      if (isKnownTemplate(data.template)) {
        setSelectedTemplate(data.template);
      }
      // Kept typing while it saved? Keep that; otherwise take the values as saved.
      if (draftRef.current === sent) setDraft(draftFrom(data));
      failedSave.current = "";
      setAutosave("saved");
      return true;
    } catch (failure) {
      failedSave.current = pendingKey;
      setAutosave("failed");
      showErrorToast(getErrorMessage(failure, "Couldn't save your changes. Please try again."));
      return false;
    } finally {
      busy.current = false;
    }
  };

  // Autosave: a moment after the last change, while the panel is open.
  useEffect(() => {
    if (!dialogOpen || !pendingKey || saveBlockers.length > 0 || saving || autosave === "saving" || pendingKey === failedSave.current) return;
    const timer = window.setTimeout(() => void saveNow(), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
    // saveNow reads the latest render; these are what should restart the wait.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, pendingKey, saveBlockers.length, saving, autosave]);

  const retrySave = () => {
    failedSave.current = "";
    void saveNow();
  };

  const handleClose = async () => {
    if (saving || uploading || autosave === "saving") return;
    if (!hasChanges) return closeNow();
    // Changes that can't be saved yet are lost on closing, so ask first.
    if (saveBlockers.length > 0) return setDiscardDialogOpen(true);
    if (await saveNow()) closeNow();
  };

  const handleEditVenue = () => {
    closeNow();
    onEditVenue?.();
  };

  /** Takes the person to whatever fixes a check: a field here, the photos, or the venue's edit page. */
  const goTo = (check: Check) => {
    setTodoAnchor(null);
    if (check.target === "edit-venue") {
      if (onEditVenue) handleEditVenue();
      return;
    }
    if (check.target === "latitude") setShowMapPin(true);
    // Wait a frame so a just-opened section exists before scrolling to it.
    window.setTimeout(() => {
      const element = document.getElementById(`publish-${check.target}`);
      element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) element.focus();
      else element?.querySelector<HTMLElement>("input, textarea, button")?.focus();
    }, 50);
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      showSuccessToast("Link copied.");
    } catch {
      showInfoToast(publicUrl);
    }
  };

  /** Publishes or unpublishes, saving any edits not yet autosaved first, then closes the panel. */
  const submit = async (action: PublicationAction) => {
    if (busy.current || !publication?.can_publish) return;
    busy.current = true;
    setSaving(true);
    try {
      let data = publication;
      if (Object.keys(detailChanges).length > 0 && saveBlockers.length === 0) {
        data = (await apiService.patch<Publication>(`${endpoint}details/`, detailChanges)).data;
      }
      const payload = action === "unpublish" ? { action } : {
        action,
        cover_photo_id: coverId,
        template: selectedTemplate,
        chema_url: current?.chema_url ?? "",
      };
      data = (await apiService.put<Publication>(endpoint, payload)).data;
      setPublication(data);
      if (isKnownTemplate(data.template)) {
        setSelectedTemplate(data.template);
      }
      setUnpublishDialogOpen(false);
      closeNow();
      const name = data.name?.trim() || "The venue";
      showSuccessToast(action === "publish" ? `${name} is now live on the website.` : `${name} is no longer on the website.`);
      if (action === "publish") {
        setPublishedModalOpen(true);
      }
    } catch (failure) {
      showErrorToast(getErrorMessage(failure));
      setUnpublishDialogOpen(false);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  /** AI help with the description. Nothing is saved until a suggestion is used and saved. */
  const askAi = async (mode: "write" | "improve") => {
    if (!current || ai?.loading) return;
    setAi({ mode, loading: true, suggestions: [], error: "" });
    try {
      const { data } = await apiService.post<{ suggestions: string[] }>(`${endpoint}description-suggestions/`, {
        mode,
        text: mode === "improve" ? current.description : "",
      });
      setAi({ mode, loading: false, suggestions: data.suggestions ?? [], error: "" });
    } catch (failure) {
      setAi({ mode, loading: false, suggestions: [], error: getErrorMessage(failure, "AI suggestions aren't available right now. Please try again.") });
    }
  };

  const uploadPhotos = async (files: FileList | null, kind: "photo" | "floor_plan" = "photo") => {
    if (!files?.length || uploading) return;
    const form = new FormData();
    Array.from(files).forEach((file) => form.append("photos", file));
    if (kind === "floor_plan") form.append("kind", "floor_plan");
    setUploading(true);
    try {
      const { data } = await apiService.post<Publication>(`${endpoint}photos/`, form);
      // Keep the details being edited; only the files come from the response.
      setPublication((before) => (before ? { ...before, photos: data.photos, extra_fields: data.extra_fields ?? before.extra_fields } : data));
      const added = files.length;
      if (kind === "floor_plan") {
        showSuccessToast(`${added} ${added === 1 ? "floor plan" : "floor plans"} added.`);
        return;
      }
      showSuccessToast(`${added} ${added === 1 ? "photo" : "photos"} added.`);
      // The first photo of a venue without a cover becomes the cover.
      if (coverId === null && data.photos?.length) setCoverId(data.photos[data.photos.length - added]?.id ?? data.photos[0].id);
    } catch (failure) {
      showErrorToast(getErrorMessage(failure, "Could not add the files. Please try again."));
    } finally {
      setUploading(false);
      if (photoInput.current) photoInput.current.value = "";
      if (planInput.current) planInput.current.value = "";
    }
  };

  const layoutOptions = publication?.layout_type_options?.length
    ? publication.layout_type_options
    : ["theater", "banquet", "cocktail", "classroom", "boardroom", "u_shape", "cabaret", "other"].map((value) => ({ value, label: formatLabel(value) }));
  const setLayout = (index: number, change: Partial<Draft["layouts"][number]>) =>
    current && set("layouts", current.layouts.map((layout, position) => (position === index ? { ...layout, ...change } : layout)));

  /** Finds existing contacts to link (needs permission to see contacts in MMS). */
  const searchContacts = async (query: string) => {
    setContactSearch({ query, loading: Boolean(query.trim()), results: [], error: "" });
    if (query.trim().length < 2) return;
    try {
      const { data } = await apiService.get<{ results?: UnknownRecord[] } | UnknownRecord[]>("/api/contacts/contact/", { params: { search: query.trim(), page_size: 8 } });
      const rows = Array.isArray(data) ? data : data.results ?? [];
      const results = rows.map((row) => {
        const name = String(row.contact_type === "company" ? row.name ?? "" : [row.first_name, row.last_name].filter(Boolean).join(" ")).trim();
        return { id: Number(row.id), name: name || "Unnamed contact", phone: String(row.phone ?? "").replace(/^n\/a$/i, ""), email: String(row.email ?? "") };
      });
      setContactSearch((before) => (before?.query === query ? { query, loading: false, results, error: "" } : before));
    } catch {
      setContactSearch((before) => (before?.query === query
        ? { query, loading: false, results: [], error: "You can't search contacts. Link them in the venue's details instead." }
        : before));
    }
  };

  const chooseFoundContact = (person: PublicContact) => {
    setFoundContacts((before) => (before.some((item) => item.id === person.id) ? before : [...before, person]));
    set("contact_id", person.id);
    setContactSearch(null);
  };

  const toggleAmenity = (amenity: string) => {
    if (!current) return;
    const on = current.amenities.some((item) => item.toLowerCase() === amenity.toLowerCase());
    set("amenities", on ? current.amenities.filter((item) => item.toLowerCase() !== amenity.toLowerCase()) : [...current.amenities, amenity]);
  };

  const addAmenity = () => {
    const amenity = newAmenity.trim().slice(0, 60);
    if (amenity && current && !current.amenities.some((item) => item.toLowerCase() === amenity.toLowerCase())) {
      set("amenities", [...current.amenities, amenity]);
    }
    setNewAmenity("");
  };

  /* ---------------- LOADING / ERROR ---------------- */

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">Loading website listing…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (loadError || !publication || !current) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Eyebrow>Website listing</Eyebrow>
        <Alert severity="warning" sx={{ mt: 1 }} action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
          Status unavailable.
        </Alert>
      </Paper>
    );
  }

  const fieldProps = (key: keyof Draft, label: string) => ({
    id: `publish-${key}`,
    label,
    fullWidth: true,
    size: "small" as const,
    value: current[key] as string,
    disabled: saving,
    error: missingKeys.has(key),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => set(key, event.target.value as Draft[typeof key]),
  });

  // Extra details from the website settings, placed with what they belong to.
  const extrasIn = (place: "basics" | "amenities" | "layouts" | "livestreaming" | "more") =>
    extraFields.filter((field) => {
      if (field.input === "files") return place === "layouts";
      if (field.group === "location") return place === "basics";
      if (field.group === "space") return place === "amenities";
      if (field.group === "livestreaming") return place === "livestreaming";
      return place === "more";
    });
  const sections = [
    ...SECTIONS,
    ...(extrasIn("livestreaming").length ? [{ key: "livestreaming", label: "Livestreaming", icon: <SensorsOutlined /> }] : []),
    ...(extrasIn("more").length ? [{ key: "more", label: "More details", icon: <TuneOutlined /> }] : []),
  ] as { key: string; label: string; icon: ReactNode }[];
  const extraInputs = (fields: ExtraField[]) =>
    fields.map((field) => (
      <ExtraFieldInput
        key={field.key}
        field={field}
        value={current.extras[field.key]}
        disabled={saving || uploading}
        onChange={(value) => set("extras", { ...current.extras, [field.key]: value })}
        onUpload={() => planInput.current?.click()}
        uploading={uploading}
      />
    ));

  // Each section's state for the list on the left: something required missing, something nice to add, or done.
  const sectionState = (key: string): "missing" | "todo" | "done" => {
    if (key === "template") return "done";
    if (key === "chema") return current?.chema_url ? "done" : "todo";
    const mine = checks.filter((check) => SECTION_OF[check.target] === key && !check.passed);
    if (mine.some((check) => check.required)) return "missing";
    return mine.length ? "todo" : "done";
  };
  const scrollToSection = (key: string) =>
    document.getElementById(`publish-section-${key}`)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  const savedStatusLine = {
    live: publication.published_at ? `Live since ${formatDate(publication.published_at)}` : "Live on the website",
    hidden: `${savedMissing.length} to fix before it shows`,
    draft: savedMissing.length ? `${savedMissing.length} ${savedMissing.length === 1 ? "thing" : "things"} left` : "Ready to publish",
    none: savedMissing.length ? `${savedMissing.length} ${savedMissing.length === 1 ? "thing" : "things"} left` : "Ready to publish",
  }[status];

  return (
    <>
      {/* =====================================================
          STATUS CARD: cover, status, progress, one button
          ===================================================== */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ position: "relative", aspectRatio: "16 / 10", bgcolor: "action.hover" }}>
          {savedCover ? (
            <Box component="img" src={savedCover.file} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: "text.disabled" }}>
              <ImageOutlined />
            </Stack>
          )}
          <StatusPill label={statusLabel} color={statusColor} sx={{ position: "absolute", top: 10, left: 10 }} />
        </Box>
        <Box sx={{ p: 1.75 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ProgressRing done={savedReadyCount} total={savedRequired.length} color={savedMissing.length ? theme.palette.warning.main : theme.palette.success.main} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={800}>Website listing</Typography>
              <Typography variant="caption" color="text.secondary">{savedStatusLine}</Typography>
            </Box>
            {rating && (
              <Stack direction="row" spacing={0.25} alignItems="center" aria-label={`Rated ${rating.average.toFixed(1)} from ${rating.count} reviews`}>
                <Star sx={{ fontSize: 15, color: "#f59e0b" }} />
                <Typography variant="caption" fontWeight={700}>{rating.average.toFixed(1)}</Typography>
                <Typography variant="caption" color="text.secondary">· {rating.count} {rating.count === 1 ? "review" : "reviews"}</Typography>
              </Stack>
            )}
          </Stack>

          {/* Website Template Info on Status Card */}
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(TEMPLATES[selectedTemplate].accent, 0.05),
              border: "1px solid",
              borderColor: alpha(TEMPLATES[selectedTemplate].accent, 0.2),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: TEMPLATES[selectedTemplate].accent,
                    display: "grid",
                    placeItems: "center",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  <PaletteOutlined sx={{ fontSize: 14 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, display: "block", lineHeight: 1 }}>
                    Website Template
                  </Typography>
                  <Typography variant="body2" fontWeight={800} noWrap sx={{ color: "text.primary", lineHeight: 1.2 }}>
                    {TEMPLATES[selectedTemplate].name}
                  </Typography>
                </Box>
              </Stack>
              <ButtonBase
                onClick={() => {
                  handleOpen();
                  setTimeout(() => scrollToSection("template"), 100);
                }}
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: 1.5,
                  fontSize: 11,
                  fontWeight: 700,
                  color: TEMPLATES[selectedTemplate].accent,
                  bgcolor: alpha(TEMPLATES[selectedTemplate].accent, 0.1),
                  "&:hover": { bgcolor: alpha(TEMPLATES[selectedTemplate].accent, 0.2) },
                  flexShrink: 0,
                }}
              >
                Change
              </ButtonBase>
            </Stack>
          </Box>
          {publication.can_publish && (
            <Button
              fullWidth
              size="small"
              variant={status === "live" ? "outlined" : "contained"}
              onClick={handleOpen}
              sx={{ mt: 1.5, textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              {publication.is_published ? "Manage listing" : publication.slug ? "Continue publishing" : "Publish to website"}
            </Button>
          )}
          {publication.is_published && publicUrl && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
              <Button fullWidth size="small" startIcon={<Launch />} href={publicUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
                View on website
              </Button>
              <Tooltip title="View publication summary">
                <IconButton size="small" aria-label="View publication summary" onClick={() => setPublishedModalOpen(true)}>
                  <PublicOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy link">
                <IconButton size="small" aria-label="Copy link" onClick={() => void copyLink()}>
                  <ContentCopy sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </Paper>

      {/* =====================================================
          PUBLISHING WINDOW: sections on the left, the form, the phone
          ===================================================== */}
      <Dialog
        open={dialogOpen}
        onClose={() => void handleClose()}
        fullWidth
        maxWidth="xl"
        fullScreen={fullScreen}
        aria-labelledby="venue-publication-title"
        slotProps={{ paper: { sx: { height: { xs: "100%", md: "92vh" }, maxHeight: { xs: "100%", md: "92vh" }, borderRadius: { xs: 0, md: 3 }, overflow: "hidden" } } }}
      >
        {/* HEADER */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography id="venue-publication-title" variant="h6" fontWeight={800} noWrap sx={{ minWidth: 0 }}>
            {current.name.trim() || "Untitled venue"}
          </Typography>
          <StatusPill label={statusLabel} color={statusColor} />
          <Box sx={{ flex: 1 }} />
          <ButtonBase
            aria-label={readyToPublish ? "Ready to publish" : `${missing.length} ${missing.length === 1 ? "thing" : "things"} left before you can publish`}
            aria-haspopup="dialog"
            onClick={(event) => setTodoAnchor(event.currentTarget)}
            sx={{
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: alpha(readyToPublish ? theme.palette.success.main : theme.palette.warning.main, 0.12),
              color: readyToPublish ? "success.dark" : "warning.dark",
              "& svg": { fontSize: 16 },
            }}
          >
            {readyToPublish ? <CheckCircle /> : <WarningAmber />}
            {readyToPublish ? "Ready" : `${missing.length} left`}
          </ButtonBase>
          <IconButton aria-label="Close publishing panel" disabled={saving || uploading || autosave === "saving"} onClick={() => void handleClose()} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* What's left, on demand */}
        <Popover
          open={Boolean(todoAnchor)}
          anchorEl={todoAnchor}
          onClose={() => setTodoAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 340, p: 1.5, borderRadius: 3, mt: 1 } } }}
        >
          {missing.length > 0 ? (
            <>
              <Eyebrow>Before you publish</Eyebrow>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {missing.map((check) => (
                  <TodoRow key={check.key} check={check} onGo={check.target === "edit-venue" && !onEditVenue ? undefined : () => goTo(check)} />
                ))}
              </Stack>
            </>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
              <CheckCircle color="success" />
              <Typography fontWeight={700}>{publication.is_published ? "Everything's in place" : "Ready to publish"}</Typography>
            </Stack>
          )}
          {niceToHave.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Eyebrow>Nice to have</Eyebrow>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {niceToHave.map((check) => (
                  <TodoRow key={check.key} check={check} soft onGo={check.target === "edit-venue" && !onEditVenue ? undefined : () => goTo(check)} />
                ))}
              </Stack>
            </Box>
          )}
        </Popover>

        {hiddenWhileLive && (
          <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5 }}>
            <Alert severity="warning" icon={<VisibilityOffOutlined />}>
              Published, but the website is hiding it until the details marked below are fixed.
            </Alert>
          </Box>
        )}

        <DialogContent sx={{ p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px minmax(0, 1fr)", lg: "180px minmax(0, 1fr) 320px" }, bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "#F6F7F9") }}>
          {/* ---------------- SECTIONS ---------------- */}
          <Box component="nav" aria-label="Sections" sx={{ display: { xs: "none", md: "block" }, p: 1.25, borderRight: 1, borderColor: "divider", bgcolor: "background.paper" }}>
            {sections.map((section) => {
              const state = sectionState(section.key);
              return (
                <ButtonBase
                  key={section.key}
                  onClick={() => scrollToSection(section.key)}
                  aria-label={`${section.label}${state === "missing" ? ", needs attention" : state === "done" ? ", done" : ""}`}
                  sx={{ width: "100%", justifyContent: "flex-start", gap: 1.25, px: 1.25, py: 1, borderRadius: 2, fontSize: 14, fontWeight: 600, color: "text.primary", "&:hover": { bgcolor: "action.hover" }, "& > svg": { fontSize: 18, color: "text.secondary" } }}
                >
                  {section.icon}
                  <Box component="span" sx={{ flex: 1, textAlign: "left" }}>{section.label}</Box>
                  <SectionDot state={state} />
                </ButtonBase>
              );
            })}
          </Box>

          {/* ---------------- FORM ---------------- */}
          <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, overflowY: { md: "auto" } }}>
            <Card id="basics" title="Basics" icon={<StorefrontOutlined />}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <TextField {...fieldProps("name", "Name")} sx={{ gridColumn: { sm: "1 / -1" } }} />
                <TextField {...fieldProps("venue_type", "Type")} select>
                  {typeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  {...fieldProps("capacity", "Capacity")}
                  type="number"
                  inputProps={{ min: 1, max: 1000000, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">guests</InputAdornment> }}
                />
                <TextField {...fieldProps("city", "City")} />
                <TextField {...fieldProps("suburb", "Area")} />
                <TextField {...fieldProps("street_address", "Street address")} sx={{ gridColumn: { sm: "1 / -1" } }} />
                {showMapPin && (
                  <>
                    <Tooltip title="Right-click the spot in Google Maps to copy its numbers" placement="top-start">
                      <TextField {...fieldProps("latitude", "Latitude")} placeholder="-17.8292" />
                    </Tooltip>
                    <TextField {...fieldProps("longitude", "Longitude")} placeholder="31.0522" error={missingKeys.has("latitude")} />
                  </>
                )}
              </Box>
              {extrasIn("basics").length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 1.5 }}>{extraInputs(extrasIn("basics"))}</Box>
              )}
              {!showMapPin && <AddLink onClick={() => setShowMapPin(true)} disabled={saving}>Map pin</AddLink>}
            </Card>

            <Card id="template" title="Website Page Template" icon={<PaletteOutlined />}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Choose which template visitors will see on muzukuru.com when published. Each card displays how your venue details, photos, and description will look.
                </Typography>
              </Box>

              {(() => {
                const venueName = current?.name.trim() || venueData.name || "Untitled venue";
                const venueTypeLabel = (typeOptions.find((option) => option.value === current?.venue_type)?.label) || (current?.venue_type ? formatLabel(current.venue_type) : "Venue");
                const venueArea = [current?.suburb, current?.city].map((part) => part?.trim()).filter(Boolean).join(", ") || "Harare, Zimbabwe";
                const venueCapacity = current?.capacity ? `Up to ${current.capacity} guests` : null;
                const venueDesc = current?.description?.trim() || "";
                const previewCover = selectedCover?.file || (photos.length > 0 ? photos[0].file : null);
                const amenitiesCount = current?.amenities?.length || 0;

                const templatesList = [
                  {
                    id: "modern" as TemplateId,
                    name: TEMPLATES.modern.name,
                    badge: TEMPLATES.modern.badge,
                    tagline: TEMPLATES.modern.tagline,
                    accent: TEMPLATES.modern.accent,
                    gradient: TEMPLATES.modern.gradient,
                    icon: <LayersOutlined sx={{ fontSize: 18 }} />,
                  },
                  {
                    id: "luxury" as TemplateId,
                    name: TEMPLATES.luxury.name,
                    badge: TEMPLATES.luxury.badge,
                    tagline: TEMPLATES.luxury.tagline,
                    accent: TEMPLATES.luxury.accent,
                    gradient: TEMPLATES.luxury.gradient,
                    icon: <DiamondOutlined sx={{ fontSize: 18 }} />,
                  },
                  {
                    id: "vibrant" as TemplateId,
                    name: TEMPLATES.vibrant.name,
                    badge: TEMPLATES.vibrant.badge,
                    tagline: TEMPLATES.vibrant.tagline,
                    accent: TEMPLATES.vibrant.accent,
                    gradient: TEMPLATES.vibrant.gradient,
                    icon: <AutoAwesome sx={{ fontSize: 18 }} />,
                  },
                  {
                    id: "studio" as TemplateId,
                    name: TEMPLATES.studio.name,
                    badge: TEMPLATES.studio.badge,
                    tagline: TEMPLATES.studio.tagline,
                    accent: TEMPLATES.studio.accent,
                    gradient: TEMPLATES.studio.gradient,
                    icon: <ArchitectureOutlined sx={{ fontSize: 18 }} />,
                  },
                ];

                return (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.75 }}>
                    {templatesList.map((tpl) => {
                      const isSelected = selectedTemplate === tpl.id;
                      return (
                        <Paper
                          key={tpl.id}
                          variant="outlined"
                          onClick={() => handleSelectTemplate(tpl.id)}
                          sx={{
                            p: 1.5,
                            borderRadius: 3,
                            cursor: "pointer",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            border: 2,
                            borderColor: isSelected ? tpl.accent : "divider",
                            bgcolor: isSelected ? alpha(tpl.accent, 0.04) : "background.paper",
                            boxShadow: isSelected ? `0 8px 24px ${alpha(tpl.accent, 0.16)}` : "0 1px 3px rgba(0,0,0,0.04)",
                            transition: "all .2s ease",
                            "&:hover": {
                              borderColor: tpl.accent,
                              transform: "translateY(-2px)",
                              boxShadow: `0 10px 28px ${alpha(tpl.accent, 0.2)}`,
                            },
                          }}
                        >
                          {/* Card Header: Icon, Name & Active Badge */}
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ color: tpl.accent, display: "flex", flexShrink: 0 }}>{tpl.icon}</Box>
                              <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ fontSize: 13, flex: 1 }}>
                                {tpl.name}
                              </Typography>
                            </Stack>
                            {isSelected ? (
                              <Box sx={{ px: 0.9, py: 0.2, borderRadius: 999, bgcolor: tpl.accent, color: "#fff", display: "flex", alignItems: "center", gap: 0.35, fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                                <CheckIcon sx={{ fontSize: 12, fontWeight: 900 }} /> Active
                              </Box>
                            ) : (
                              <Box sx={{ px: 0.75, py: 0.15, borderRadius: 999, bgcolor: "action.hover", color: "text.secondary", fontSize: 9.5, fontWeight: 700, flexShrink: 0 }}>
                                {tpl.id === "modern" ? "Default" : tpl.id === "luxury" ? "Editorial" : tpl.id === "vibrant" ? "Dynamic" : "Specs"}
                              </Box>
                            )}
                          </Stack>

                          {/* Live Miniature Venue Preview Viewport */}
                          <Box
                            sx={{
                              height: 205,
                              borderRadius: 2,
                              overflow: "hidden",
                              border: 1,
                              borderColor: isSelected ? alpha(tpl.accent, 0.35) : "divider",
                              bgcolor: tpl.id === "luxury" ? "#FCFAF6" : tpl.id === "studio" ? "#FAFAFA" : tpl.id === "vibrant" ? "#FFF8F1" : "#FFFFFF",
                              display: "flex",
                              flexDirection: "column",
                              position: "relative",
                              boxShadow: isSelected ? `inset 0 0 0 1px ${alpha(tpl.accent, 0.2)}` : "none",
                            }}
                          >
                            {/* 1. MODERN MINIMALIST VIEWPORT */}
                            {tpl.id === "modern" && (
                              <>
                                <Box sx={{ position: "relative", height: 86, bgcolor: "#1E293B", overflow: "hidden" }}>
                                  {previewCover ? (
                                    <>
                                      <Box component="img" src={previewCover} alt={venueName} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.1) 0%, rgba(15,23,42,0.6) 100%)" }} />
                                    </>
                                  ) : (
                                    <Box sx={{ width: "100%", height: "100%", background: tpl.gradient, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", gap: 0.25 }}>
                                      <StorefrontOutlined sx={{ fontSize: 22, opacity: 0.85 }} />
                                      <Typography sx={{ fontSize: 9, fontWeight: 700, opacity: 0.9 }}>{venueTypeLabel} Photo</Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ position: "absolute", top: 6, left: 6, px: 0.75, py: 0.2, borderRadius: 999, bgcolor: "rgba(15,23,42,0.75)", color: "#fff", fontSize: 8, fontWeight: 800, backdropFilter: "blur(4px)" }}>
                                    {venueTypeLabel.toUpperCase()}
                                  </Box>
                                </Box>
                                <Box sx={{ p: 1, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", bgcolor: "#fff" }}>
                                  <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#0F172A", lineHeight: 1.25 }} noWrap>
                                      {venueName}
                                    </Typography>
                                    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ color: "#64748B", mt: 0.25 }}>
                                      <LocationOnOutlined sx={{ fontSize: 11, color: tpl.accent }} />
                                      <Typography noWrap sx={{ fontSize: 9.5, color: "#64748B" }}>{venueArea}</Typography>
                                    </Stack>
                                    <Typography sx={{ mt: 0.4, fontSize: 8.5, color: "#475569", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                      {venueDesc || "Clean lines, high-contrast clarity, and contemporary presentation."}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                                    {venueCapacity && <Box sx={{ px: 0.6, py: 0.2, borderRadius: 1, bgcolor: "#F1F5F9", color: "#334155", fontSize: 8, fontWeight: 700 }}>{venueCapacity}</Box>}
                                    <Box sx={{ px: 0.6, py: 0.2, borderRadius: 1, bgcolor: "#F1F5F9", color: "#334155", fontSize: 8, fontWeight: 700 }}>{amenitiesCount} amenities</Box>
                                  </Box>
                                </Box>
                              </>
                            )}

                            {/* 2. LUXURY HERITAGE VIEWPORT */}
                            {tpl.id === "luxury" && (
                              <Box sx={{ p: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", bgcolor: "#FCFAF6", fontFamily: "Georgia, serif" }}>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: "Georgia, serif", color: "#451A03", lineHeight: 1.2, mt: 0.3 }} noWrap>
                                    {venueName}
                                  </Typography>
                                  <Typography sx={{ fontSize: 8.5, fontFamily: "Georgia, serif", fontStyle: "italic", color: "#92400E" }} noWrap>
                                    {venueArea}{venueCapacity ? ` · ${venueCapacity}` : ""}
                                  </Typography>
                                </Box>

                                {/* Gilded Picture Frame */}
                                <Box sx={{ my: 0.35, p: "2px", borderRadius: "6px", background: "linear-gradient(180deg, #FDE68A, #D97706)", boxShadow: "0 2px 6px rgba(120,53,15,0.12)" }}>
                                  <Box sx={{ height: 60, borderRadius: "4px", overflow: "hidden", position: "relative", bgcolor: "#451A03" }}>
                                    {previewCover ? (
                                      <Box component="img" src={previewCover} alt={venueName} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                    ) : (
                                      <Box sx={{ width: "100%", height: "100%", background: tpl.gradient, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: "#FEF3C7" }}>
                                        <DiamondOutlined sx={{ fontSize: 16 }} />
                                        <Typography sx={{ fontSize: 8.5, fontFamily: "Georgia, serif", fontWeight: 700 }}>Gilded Gallery</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>

                                <Typography sx={{ fontSize: 8.5, fontFamily: "Georgia, serif", color: "#78350F", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", textAlign: "center" }}>
                                  {venueDesc || "Grand editorial hospitality format with opulent serif styling and gold accents."}
                                </Typography>

                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mt: 0.35 }}>
                                  <Box sx={{ p: 0.35, borderRadius: "4px", bgcolor: "#FFFDF9", border: "1px solid #FDE68A", textAlign: "center", fontSize: 8, fontWeight: 700, color: "#78350F", fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    ⚜ {venueCapacity || "VIP Heritage"}
                                  </Box>
                                  <Box sx={{ p: 0.35, borderRadius: "4px", bgcolor: "#FFFDF9", border: "1px solid #FDE68A", textAlign: "center", fontSize: 8, fontWeight: 700, color: "#78350F", fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    ♔ Concierge
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {/* 3. VIBRANT SHOWCASE VIEWPORT */}
                            {tpl.id === "vibrant" && (
                              <Box sx={{ p: 0.75, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", background: "linear-gradient(180deg, #FFF8F1 0%, #FFFFFF 100%)" }}>
                                <Box sx={{ position: "relative", height: 72, borderRadius: "10px", overflow: "hidden", border: "1.5px solid #E8D8CB", bgcolor: "#5C3423", boxShadow: "0 3px 10px rgba(114,78,65,0.12)" }}>
                                  {previewCover ? (
                                    <>
                                      <Box component="img" src={previewCover} alt={venueName} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(92,52,35,0.6) 100%)" }} />
                                    </>
                                  ) : (
                                    <Box sx={{ width: "100%", height: "100%", background: tpl.gradient, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: "#FDF2E9" }}>
                                      <AutoAwesome sx={{ fontSize: 16 }} />
                                      <Typography sx={{ fontSize: 8.5, fontWeight: 800 }}>⚡ Dynamic Media</Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ position: "absolute", top: 4, left: 4, px: 0.6, py: 0.15, borderRadius: 999, bgcolor: "rgba(255,255,255,0.92)", color: "#724E41", fontSize: 7.5, fontWeight: 800 }}>
                                    ⚡ {venueTypeLabel}
                                  </Box>
                                </Box>

                                {/* Overlapping Floating Glass Card */}
                                <Box sx={{ mx: 0.5, mt: -2, position: "relative", zIndex: 1, p: 0.65, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.95)", border: "1px solid #E8D8CB", boxShadow: "0 4px 10px rgba(114,78,65,0.08)" }}>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 900, color: "#0F172A", lineHeight: 1.2 }} noWrap>
                                    {venueName}
                                  </Typography>
                                  <Stack direction="row" spacing={0.35} sx={{ mt: 0.25 }}>
                                    <Box sx={{ px: 0.5, py: 0.1, borderRadius: 999, bgcolor: "#FFF8F1", color: "#5C3423", border: "1px solid #E8D8CB", fontSize: 7.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                                      📍 {venueArea}
                                    </Box>
                                    {venueCapacity && (
                                      <Box sx={{ px: 0.5, py: 0.1, borderRadius: 999, bgcolor: "#FFF8F1", color: "#5C3423", border: "1px solid #E8D8CB", fontSize: 7.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                                        👥 {venueCapacity}
                                      </Box>
                                    )}
                                  </Stack>
                                </Box>

                                <Typography sx={{ px: 0.5, mt: 0.35, fontSize: 8.5, color: "#475569", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {venueDesc || "Dynamic event presentation with floating glassmorphic cards and responsive bento grid."}
                                </Typography>

                                <Box sx={{ px: 0.5, pb: 0.25 }}>
                                  <Box sx={{ display: "inline-block", px: 0.6, py: 0.15, borderRadius: 999, bgcolor: "#FDF2E9", color: "#724E41", border: "1px solid #E8D8CB", fontSize: 7.5, fontWeight: 700 }}>
                                    ✨ {amenitiesCount} perks available
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            {/* 4. SPLIT STUDIO VIEWPORT */}
                            {tpl.id === "studio" && (
                              <Box sx={{ p: 0.75, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", bgcolor: "#FAFAFA", fontFamily: "monospace" }}>
                                <Box sx={{ pb: 0.3, borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography sx={{ fontSize: 7.5, fontFamily: "monospace", letterSpacing: 0.8, color: "#64748B" }}>
                                    [ DIRECTORY ]
                                  </Typography>
                                  <Typography sx={{ fontSize: 7.5, fontFamily: "monospace", letterSpacing: 0.8, color: "#94A3B8" }}>
                                    STUDIO // SPEC
                                  </Typography>
                                </Box>

                                <Box sx={{ position: "relative", height: 58, borderRadius: "4px", border: "1px solid #CBD5E1", overflow: "hidden", bgcolor: "#0F172A", my: 0.35 }}>
                                  {previewCover ? (
                                    <Box component="img" src={previewCover} alt={venueName} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                  ) : (
                                    <Box sx={{ width: "100%", height: "100%", background: tpl.gradient, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, color: "#CBD5E1" }}>
                                      <ArchitectureOutlined sx={{ fontSize: 16 }} />
                                      <Typography sx={{ fontSize: 8, fontFamily: "monospace", fontWeight: 700 }}>FIG 01 // SPEC</Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ position: "absolute", top: 3, left: 3, px: 0.5, py: 0.1, borderRadius: "2px", bgcolor: "#0F172A", color: "#fff", fontSize: 7, fontFamily: "monospace", fontWeight: 700 }}>
                                    FIG 01 // {venueTypeLabel.toUpperCase()}
                                  </Box>
                                </Box>

                                <Box>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 900, color: "#0F172A", lineHeight: 1.2 }} noWrap>
                                    {venueName}
                                  </Typography>
                                  <Box sx={{ mt: 0.3, p: 0.35, borderRadius: "3px", border: "1px solid #CBD5E1", bgcolor: "#FFFFFF" }}>
                                    <Typography sx={{ fontSize: 7.5, fontFamily: "monospace", color: "#64748B" }} noWrap>
                                      LOC // {venueArea}
                                    </Typography>
                                    <Typography sx={{ fontSize: 7.5, fontFamily: "monospace", color: "#64748B" }} noWrap>
                                      CAP // {venueCapacity || "UPON REQUEST"}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Typography sx={{ fontSize: 8, fontFamily: "monospace", color: "#475569", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {venueDesc || "Architectural split-screen layout with sticky media showcase & editorial technical specs."}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Footer & Select Button */}
                          <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
                            <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {tpl.tagline}
                            </Typography>
                            <Button
                              size="small"
                              variant={isSelected ? "contained" : "outlined"}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectTemplate(tpl.id);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                bgcolor: isSelected ? tpl.accent : "transparent",
                                borderColor: isSelected ? tpl.accent : "divider",
                                color: isSelected ? "#fff" : "text.primary",
                                "&:hover": {
                                  bgcolor: isSelected ? tpl.accent : alpha(tpl.accent, 0.08),
                                  borderColor: tpl.accent,
                                },
                              }}
                            >
                              {isSelected ? "Active Template" : "Select Template"}
                            </Button>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                );
              })()}
            </Card>

            <Card id="description" title="Description" icon={<NotesOutlined />}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  {...fieldProps("description", "Description")}
                  label={undefined}
                  inputProps={{ "aria-label": "Description", maxLength: DESCRIPTION_LIMIT }}
                  multiline
                  minRows={5}
                  placeholder="What's the venue like?"
                  sx={{ "& .MuiInputBase-root": { pb: 5.5 } }}
                />
                {/* AI help, inside the box. */}
                <Stack direction="row" spacing={0.75} sx={{ position: "absolute", right: 10, bottom: 10 }}>
                  <AiButton icon={<AutoAwesome />} disabled={saving || Boolean(ai?.loading)} onClick={() => void askAi("write")} label="Write with AI">Write</AiButton>
                  <Tooltip title={current.description.trim().length < 20 ? "Write a little first" : ""}>
                    <span>
                      <AiButton icon={<AutoFixHigh />} disabled={saving || Boolean(ai?.loading) || current.description.trim().length < 20} onClick={() => void askAi("improve")} label="Improve my text">Improve</AiButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
              <Collapse in={Boolean(ai)} unmountOnExit>
                {ai && (
                  <Box sx={{ mt: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: "#6D28D9" }}>
                        {ai.loading ? "Writing…" : "AI suggestions · check the facts"}
                      </Typography>
                      <IconButton size="small" aria-label="Close suggestions" onClick={() => setAi(null)}><Close sx={{ fontSize: 16 }} /></IconButton>
                    </Stack>
                    {ai.error && <Alert severity="warning">{ai.error}</Alert>}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(3, 1fr)" }, gap: 1 }}>
                      {ai.loading
                        ? [0, 1, 2].map((index) => <Skeleton key={index} variant="rounded" height={110} />)
                        : ai.suggestions.map((suggestion, index) => (
                            <ButtonBase
                              key={index}
                              aria-label="Use this"
                              onClick={() => {
                                set("description", suggestion);
                                setAi(null);
                              }}
                              sx={{ display: "block", textAlign: "left", p: 1.25, borderRadius: 2, border: 1, borderColor: "#DDD6FE", bgcolor: "#FCFBFF", "&:hover": { borderColor: "#7C3AED", bgcolor: "#F5F3FF" } }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>{suggestion}</Typography>
                              <Typography variant="caption" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.75, color: "#6D28D9" }}>
                                <CheckIcon sx={{ fontSize: 14 }} /> Use this
                              </Typography>
                            </ButtonBase>
                          ))}
                    </Box>
                  </Box>
                )}
              </Collapse>
            </Card>

            <Card id="amenities" title="Amenities" icon={<ChecklistOutlined />}>
              <Box id="publish-amenities" sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {amenityChoices.map((amenity) => {
                  const on = current.amenities.some((item) => item.toLowerCase() === amenity.toLowerCase());
                  return (
                    <Chip
                      key={amenity}
                      label={amenity}
                      clickable
                      disabled={saving}
                      aria-pressed={on}
                      onClick={() => toggleAmenity(amenity)}
                      color={on ? "primary" : "default"}
                      variant={on ? "filled" : "outlined"}
                      sx={{ fontWeight: 600 }}
                    />
                  );
                })}
                {addingAmenity ? (
                  <TextField
                    autoFocus
                    size="small"
                    value={newAmenity}
                    disabled={saving}
                    onChange={(event) => setNewAmenity(event.target.value)}
                    onBlur={() => {
                      addAmenity();
                      setAddingAmenity(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        addAmenity();
                      }
                      if (event.key === "Escape") setAddingAmenity(false);
                    }}
                    inputProps={{ "aria-label": "Add another amenity", maxLength: 60 }}
                    sx={{ width: 170, "& .MuiInputBase-root": { borderRadius: 999, height: 32 } }}
                  />
                ) : (
                  <Chip label="+ Add" variant="outlined" clickable disabled={saving} onClick={() => setAddingAmenity(true)} sx={{ borderStyle: "dashed", color: "text.secondary", fontWeight: 600 }} />
                )}
              </Box>
              {extrasIn("amenities").length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 2 }}>{extraInputs(extrasIn("amenities"))}</Box>
              )}
            </Card>

            <Card id="photos" title="Photos" icon={<PhotoLibraryOutlined />}>
              <Box id="publish-photos">
                <Box id="publish-cover" sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" }, gap: 1 }}>
                  {photos.map((photo, index) => {
                    const isCover = coverId === photo.id;
                    return (
                      <Tooltip key={photo.id} title={isCover ? "Cover photo" : "Make this the cover"}>
                        <ButtonBase
                          aria-label={isCover ? "Cover photo" : "Set as cover"}
                          aria-pressed={isCover}
                          disabled={saving}
                          onClick={() => setCoverId(photo.id)}
                          sx={{
                            position: "relative",
                            aspectRatio: "1",
                            borderRadius: 2,
                            overflow: "hidden",
                            outline: isCover ? `3px solid ${theme.palette.primary.main}` : "1px solid",
                            outlineColor: isCover ? undefined : "divider",
                            outlineOffset: isCover ? 2 : 0,
                            transition: "transform .15s",
                            "&:hover": isCover ? {} : { transform: "translateY(-2px)" },
                          }}
                        >
                          <Box component="img" src={photo.file} alt={`Photo ${index + 1}`} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          {isCover && (
                            <Box sx={{ position: "absolute", top: 6, left: 6, width: 22, height: 22, borderRadius: "50%", bgcolor: "primary.main", color: "#fff", display: "grid", placeItems: "center" }}>
                              <Star sx={{ fontSize: 14 }} />
                            </Box>
                          )}
                        </ButtonBase>
                      </Tooltip>
                    );
                  })}
                  <Tooltip title="Add photos">
                    <ButtonBase
                      aria-label="Add photos"
                      disabled={saving || uploading}
                      onClick={() => photoInput.current?.click()}
                      sx={{ aspectRatio: "1", borderRadius: 2, border: "2px dashed", borderColor: "divider", color: "text.secondary", "&:hover": { borderColor: "primary.main", color: "primary.main" } }}
                    >
                      {uploading ? <CircularProgress size={22} /> : <AddPhotoAlternateOutlined />}
                    </ButtonBase>
                  </Tooltip>
                </Box>
                <input
                  ref={photoInput}
                  hidden
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                  data-testid="publish-photo-input"
                  onChange={(event) => void uploadPhotos(event.target.files)}
                />
                {coverChanged && savedCoverId !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    New cover chosen ·{" "}
                    <ButtonBase onClick={() => setCoverId(savedCoverId)} sx={{ fontSize: "inherit", fontWeight: 700, color: "primary.main", verticalAlign: "baseline" }}>Undo</ButtonBase>
                  </Typography>
                )}
              </Box>
            </Card>

            <Card id="layouts" title="Layouts" icon={<ChairOutlined />}>
              <Stack id="publish-layouts" spacing={1}>
                {current.layouts.map((layout, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      size="small"
                      label="Style"
                      value={layout.layout_type}
                      disabled={saving}
                      error={!layout.layout_type}
                      onChange={(event) => setLayout(index, { layout_type: event.target.value })}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {layoutOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Guests"
                      type="number"
                      value={layout.capacity}
                      disabled={saving}
                      error={!(Number(layout.capacity) >= 1)}
                      onChange={(event) => setLayout(index, { capacity: event.target.value })}
                      inputProps={{ min: 1, max: 1000000, step: 1, "aria-label": `Layout ${index + 1} guests` }}
                      sx={{ width: 110 }}
                    />
                    <IconButton
                      aria-label={`Remove layout ${index + 1}`}
                      disabled={saving}
                      onClick={() => set("layouts", current.layouts.filter((_, position) => position !== index))}
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <AddLink
                  onClick={() => set("layouts", [...current.layouts, { layout_type: "", capacity: "" }])}
                  disabled={saving || current.layouts.length >= 20}
                >
                  Add layout
                </AddLink>
                {extrasIn("layouts").length > 0 && <Box sx={{ pt: 0.5 }}>{extraInputs(extrasIn("layouts"))}</Box>}
                <input
                  ref={planInput}
                  hidden
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif,application/pdf"
                  data-testid="publish-plan-input"
                  onChange={(event) => void uploadPhotos(event.target.files, "floor_plan")}
                />
                {(publication.rooms ?? []).length > 0 && (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.5, flexWrap: "wrap", rowGap: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Rooms</Typography>
                    {(publication.rooms ?? []).map((room) => (
                      <Chip key={room.name} size="small" variant="outlined" label={room.capacity ? `${room.name} · ${room.capacity} guests` : room.name} />
                    ))}
                    {onEditVenue && (
                      <Tooltip title="Rooms are venues of their own: change them in the venue's details">
                        <IconButton size="small" aria-label="Edit in venue details" onClick={handleEditVenue}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Stack>
            </Card>

            {extrasIn("livestreaming").length > 0 && (
              <Card id="livestreaming" title="Livestreaming" icon={<SensorsOutlined />}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>{extraInputs(extrasIn("livestreaming"))}</Box>
              </Card>
            )}

            {extrasIn("more").length > 0 && (
              <Card id="more" title="More details" icon={<TuneOutlined />}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>{extraInputs(extrasIn("more"))}</Box>
              </Card>
            )}

            <Card id="contact" title="Contact" icon={<PersonOutline />}>
              <Stack id="publish-contact_id" spacing={0.75}>
                {contactChoices.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No contacts linked yet.</Typography>
                )}
                {contactChoices.map((person) => {
                  const chosen = shownContact?.id === person.id;
                  const isNew = !(publication.contacts ?? []).some((linked) => linked.id === person.id);
                  return (
                    <ButtonBase
                      key={person.id}
                      role="radio"
                      aria-checked={chosen}
                      aria-label={`Show ${person.name}`}
                      disabled={saving}
                      onClick={() => set("contact_id", person.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1.25,
                        p: 1,
                        borderRadius: 2,
                        border: 1,
                        borderColor: chosen ? "primary.main" : "divider",
                        bgcolor: chosen ? alpha(theme.palette.primary.main, 0.05) : "transparent",
                        textAlign: "left",
                        "&:hover": { borderColor: "primary.main" },
                      }}
                    >
                      <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "action.hover", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                        {initials(person.name)}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {person.name}
                          {isNew && <Typography component="span" variant="caption" color="primary.main" fontWeight={700}> · will be linked</Typography>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {[person.phone, person.email].filter(Boolean).join(" · ") || "No phone or email"}
                        </Typography>
                      </Box>
                      {chosen ? <RadioButtonChecked color="primary" sx={{ fontSize: 20 }} /> : <RadioButtonUnchecked sx={{ fontSize: 20, color: "text.disabled" }} />}
                    </ButtonBase>
                  );
                })}
                {contactSearch === null ? (
                  <AddLink onClick={() => setContactSearch({ query: "", loading: false, results: [], error: "" })} disabled={saving}>Link another</AddLink>
                ) : (
                  <Box>
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      placeholder="Search contacts"
                      value={contactSearch.query}
                      onChange={(event) => void searchContacts(event.target.value)}
                      inputProps={{ "aria-label": "Search contacts" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {contactSearch.loading ? <CircularProgress size={16} /> : (
                              <IconButton size="small" aria-label="Close contact search" onClick={() => setContactSearch(null)}><Close sx={{ fontSize: 18 }} /></IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                    {contactSearch.error && <Typography variant="caption" color="warning.dark" sx={{ display: "block", mt: 0.75 }}>{contactSearch.error}</Typography>}
                    {contactSearch.results.length > 0 && (
                      <Paper variant="outlined" sx={{ mt: 0.75, borderRadius: 2, overflow: "hidden" }}>
                        {contactSearch.results.map((person) => (
                          <MenuItem key={person.id} onClick={() => chooseFoundContact(person)} sx={{ display: "block", py: 1 }}>
                            <Typography variant="body2" fontWeight={700}>{person.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{[person.phone, person.email].filter(Boolean).join(" · ") || "No phone or email"}</Typography>
                          </MenuItem>
                        ))}
                      </Paper>
                    )}
                    {!contactSearch.loading && !contactSearch.error && contactSearch.query.trim().length >= 2 && contactSearch.results.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>No contacts match.</Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </Card>

            <Card id="chema" title="Chema Fundraiser" icon={<VolunteerActivismOutlined />}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Link a campaign from <strong>chema.co.zw</strong> to display on this venue.
              </Typography>

              <TextField
                id="publish-chema_url"
                label="Chema Embed Link or Code"
                fullWidth
                size="small"
                value={current.chema_url}
                disabled={saving}
                placeholder="https://chema.co.zw/embed/... or paste <iframe>"
                onChange={(e) => {
                  const raw = e.target.value;
                  // If user pasted an iframe tag, auto-extract the src attribute
                  const match = raw.match(/src=["']([^"']+)["']/i);
                  const clean = match ? match[1] : raw;
                  set("chema_url", clean);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VolunteerActivismOutlined sx={{ fontSize: 18, color: current.chema_url ? "#059669" : "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: current.chema_url ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => set("chema_url", "")} aria-label="Clear Chema link">
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                helperText={!current.chema_url ? "Paste campaign link or embed code from chema.co.zw" : undefined}
              />

              {current.chema_url.trim() && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: alpha("#059669", 0.04), border: "1px solid", borderColor: alpha("#059669", 0.2) }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#059669", color: "#fff", display: "grid", placeItems: "center" }}>
                        <CheckIcon sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#065f46" }}>
                        Preview
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="text"
                      href={current.chema_url.startsWith("http") ? current.chema_url : `https://${current.chema_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<Launch sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#059669" }}
                    >
                      Open on Chema
                    </Button>
                  </Stack>

                  {/* Responsive Iframe Container */}
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 460,
                      mx: "auto",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #cbd5e1",
                      bgcolor: "#ffffff",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Box
                      component="iframe"
                      src={current.chema_url.startsWith("http") ? current.chema_url : `https://${current.chema_url}`}
                      title="Chema Fundraiser Preview"
                      sx={{
                        width: "100%",
                        height: 520,
                        border: 0,
                        display: "block",
                        bgcolor: "transparent",
                      }}
                      allow="payment"
                    />
                  </Box>
                </Box>
              )}
            </Card>
          </Stack>

          {/* ---------------- PHONE ---------------- */}
          <Box sx={{ p: 2.5, borderLeft: { lg: 1 }, borderColor: "divider", gridColumn: { md: "2", lg: "auto" }, overflowY: { lg: "auto" } }}>
            {/* Quick Template Switcher above Phone */}
            <Box sx={{ maxWidth: 330, mx: "auto", mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.8, textTransform: "uppercase", color: "text.secondary" }}>
                  Template Live Preview
                </Typography>
                <Chip
                  size="small"
                  label={TEMPLATES[selectedTemplate].name}
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    height: 20,
                    bgcolor: alpha(TEMPLATES[selectedTemplate].accent, 0.12),
                    color: TEMPLATES[selectedTemplate].accent,
                  }}
                />
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, p: 0.5, bgcolor: "action.hover", borderRadius: 2.5 }}>
                {(Object.keys(TEMPLATES) as TemplateId[]).map((tid) => {
                  const active = selectedTemplate === tid;
                  const t = TEMPLATES[tid];
                  return (
                    <ButtonBase
                      key={tid}
                      onClick={() => handleSelectTemplate(tid)}
                      sx={{
                        py: 0.65,
                        px: 0.5,
                        borderRadius: 2,
                        fontSize: 11,
                        fontWeight: active ? 800 : 600,
                        color: active ? "#fff" : "text.secondary",
                        bgcolor: active ? t.accent : "transparent",
                        transition: "all .15s ease",
                        boxShadow: active ? `0 2px 8px ${alpha(t.accent, 0.35)}` : "none",
                        "&:hover": active ? {} : { color: "text.primary", bgcolor: "action.selected" },
                      }}
                    >
                      {tid === "modern" ? "Default" : tid === "luxury" ? "Luxury" : tid === "vibrant" ? "Vibrant" : "Studio"}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>

            <WebsitePreview
              template={selectedTemplate}
              modes={["visitor", "member"]}
              fields={visibilityFields}
              audienceOf={(field) => field.audience}
              sections={publication.sections?.length ? publication.sections : DEFAULT_SECTIONS}
              order={publication.section_order?.length ? publication.section_order : DEFAULT_SECTIONS.map((section) => section.key)}
              unlocks={unlockList(visibilityFields, (field) => field.audience)}
              venue={{
                name: current.name.trim() || "Untitled venue",
                type: (typeOptions.find((option) => option.value === current.venue_type)?.label) || (current.venue_type ? formatLabel(current.venue_type) : "Venue"),
                area: [current.suburb, current.city].map((part) => part.trim()).filter(Boolean).join(", "),
                address: current.street_address,
                capacity: Number(current.capacity) || null,
                coverUrl: selectedCover?.file ?? (photos.length > 0 ? photos[0].file : null),
                photoCount: photos.length,
                description: current.description,
                amenities: current.amenities,
                layouts: current.layouts.filter((layout) => layout.layout_type).map((layout) => ({ name: formatLabel(layout.layout_type), capacity: Number(layout.capacity) || null })),
                rooms: publication.rooms ?? [],
                contactName: shownContact?.name ?? publication.contact_name ?? "",
                contactPhone: shownContact ? shownContact.phone : publication.contact_phone,
                contactEmail: shownContact ? shownContact.email : publication.contact_email,
                rating,
                chemaUrl: current.chema_url,
              }}
            />
            {publicUrl && publication.is_published && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.5, maxWidth: 330, mx: "auto" }}>
                <Typography variant="body2" color="primary.main" noWrap sx={{ flex: 1, minWidth: 0 }}>{publicUrl.replace(/^https?:\/\//, "")}</Typography>
                <Tooltip title="Copy link">
                  <IconButton size="small" aria-label="Copy link" onClick={() => void copyLink()}><ContentCopy sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Open">
                  <IconButton size="small" aria-label="Open the live listing" href={publicUrl} target="_blank" rel="noopener noreferrer"><Launch sx={{ fontSize: 16 }} /></IconButton>
                </Tooltip>
              </Stack>
            )}
          </Box>
        </DialogContent>

        {/* FOOTER */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, borderTop: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {publication.is_published && (
            <Button color="error" disabled={saving} onClick={() => setUnpublishDialogOpen(true)} sx={{ textTransform: "none" }}>Unpublish</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <SaveState
            state={autosave === "saving" || (hasChanges && saveBlockers.length === 0 && autosave !== "failed") ? "saving" : autosave === "failed" ? "failed" : hasChanges ? "blocked" : autosave === "saved" ? "saved" : "idle"}
            blocker={saveBlockers[0]?.todo}
            onRetry={retrySave}
          />
          <Button color="inherit" disabled={saving || uploading || autosave === "saving"} onClick={() => void handleClose()} sx={{ textTransform: "none" }}>
            {publication.is_published ? "Done" : "Close"}
          </Button>
          {!publication.is_published && (
            <Tooltip title={readyToPublish ? "" : `${missing.length} left: see the list at the top`}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<PublicOutlined />}
                  disabled={saving || uploading || autosave === "saving" || !readyToPublish}
                  onClick={() => void submit("publish")}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                >
                  {saving ? "Publishing…" : "Publish venue"}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Dialog>

      {/* DISCARD CONFIRMATION */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Some changes aren&apos;t saved</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {saveBlockers[0]?.todo ?? "Finish the marked fields"} to save them. Closing now leaves them out.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDiscardDialogOpen(false)} sx={{ textTransform: "none" }}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={closeNow} sx={{ textTransform: "none", fontWeight: 700 }}>Close anyway</Button>
        </DialogActions>
      </Dialog>

      {/* UNPUBLISH CONFIRMATION */}
      <Dialog open={unpublishDialogOpen} onClose={() => !saving && setUnpublishDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Unpublish {publication.name}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">It leaves muzukuru.com straight away. You can publish it again later with the same link.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={saving} onClick={() => setUnpublishDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={saving} onClick={() => void submit("unpublish")} sx={{ textTransform: "none", fontWeight: 700 }}>
            {saving ? "Unpublishing…" : "Unpublish venue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PUBLISHED SUCCESS MODAL */}
      <VenuePublishedModal
        open={publishedModalOpen}
        onClose={() => setPublishedModalOpen(false)}
        venueName={publication?.name || venueData.name || "Venue"}
        venueType={formatLabel(publication?.venue_type || current?.venue_type || "")}
        city={publication?.city || current?.city || ""}
        publicUrl={publicUrl}
        coverPhotoUrl={photos.find((p) => p.id === (publication?.cover_photo_id ?? coverId))?.file || null}
        templateName={TEMPLATES[selectedTemplate]?.name}
        templateAccent={TEMPLATES[selectedTemplate]?.accent}
      />
    </>
  );
}

/* =========================================================
   SMALL PIECES
   ========================================================= */

function Eyebrow({ children, color = "text.secondary" }: { children: ReactNode; color?: string }) {
  return <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color }}>{children}</Typography>;
}

/** The autosave line in the footer: saving, saved, can't save yet, or failed (with retry). */
function SaveState({ state, blocker, onRetry }: { state: "idle" | "saving" | "saved" | "blocked" | "failed"; blocker?: string; onRetry: () => void }) {
  const text = {
    idle: "Changes save automatically",
    saving: "Saving…",
    saved: "All changes saved",
    blocked: blocker ? `Not saved yet: ${blocker.charAt(0).toLowerCase()}${blocker.slice(1)}` : "Not saved yet",
    failed: "Couldn't save",
  }[state];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" role="status" aria-live="polite" sx={{ mr: 0.5, minWidth: 0 }}>
      {state === "saving" && <CircularProgress size={12} thickness={5} />}
      {state === "saved" && <CloudDoneOutlined sx={{ fontSize: 16, color: "success.main" }} />}
      {(state === "blocked" || state === "failed") && <WarningAmber sx={{ fontSize: 16, color: state === "failed" ? "error.main" : "warning.main" }} />}
      <Typography variant="caption" color={state === "failed" ? "error.main" : "text.secondary"} noWrap>{text}</Typography>
      {state === "failed" && (
        <Button size="small" onClick={onRetry} sx={{ textTransform: "none", minWidth: 0, py: 0, fontSize: 12 }}>Retry</Button>
      )}
    </Stack>
  );
}

function StatusPill({ label, color, sx }: { label: string; color: string; sx?: object }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(0,0,0,.12)",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      {label}
    </Box>
  );
}

/** A white card for one part of the form: an icon, a title and the fields. */
function Card({ id, title, icon, children }: { id: string; title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <Paper id={`publish-section-${id}`} elevation={0} sx={{ p: { xs: 1.75, sm: 2 }, borderRadius: 3, border: 1, borderColor: "divider", scrollMarginTop: 16 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "action.hover", display: "grid", placeItems: "center", color: "text.secondary", "& svg": { fontSize: 17 } }}>{icon}</Box>
        <Typography fontWeight={800}>{title}</Typography>
      </Stack>
      {children}
    </Paper>
  );
}

/** One extra detail from the website settings, with the input its type needs. */
function ExtraFieldInput({
  field,
  value,
  disabled,
  onChange,
  onUpload,
  uploading,
}: {
  field: ExtraField;
  value: string | boolean | undefined;
  disabled: boolean;
  onChange: (value: string | boolean) => void;
  onUpload: () => void;
  uploading: boolean;
}) {
  if (field.input === "files") {
    const files = Array.isArray(field.value) ? (field.value as { id: number; name: string; url: string }[]) : [];
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.75, gridColumn: "1 / -1" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{field.label}</Typography>
        {files.map((file) => (
          <Chip key={file.id} size="small" variant="outlined" component="a" href={file.url} target="_blank" rel="noopener noreferrer" clickable label={file.name} icon={<InsertDriveFileOutlined />} />
        ))}
        <Chip size="small" variant="outlined" clickable disabled={disabled} onClick={onUpload} label={uploading ? "Adding…" : `+ Add ${field.label.toLowerCase().replace(/s$/, "")}`} sx={{ borderStyle: "dashed" }} />
      </Stack>
    );
  }
  if (field.input === "yesno") {
    return (
      <FormControlLabel
        control={<Switch checked={Boolean(value)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />}
        label={field.label}
        sx={{ m: 0 }}
      />
    );
  }
  if ((field.input === "country" || field.input === "timezone") && field.choices) {
    const choice = field.choices.find((option) => option.value === value);
    return (
      <Autocomplete
        size="small"
        options={field.choices}
        value={choice}
        disabled={disabled}
        disableClearable
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        onChange={(_, option) => option && onChange(option.value)}
        renderInput={(params) => <TextField {...params} label={field.label} />}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      label={field.label}
      value={typeof value === "string" ? value : ""}
      disabled={disabled}
      type={field.input === "number" || field.input === "decimal" ? "number" : "text"}
      multiline={field.input === "longtext"}
      minRows={field.input === "longtext" ? 2 : undefined}
      inputProps={field.input === "number" ? { min: 0, step: 1 } : field.input === "decimal" ? { min: 0, step: "any" } : { maxLength: field.input === "longtext" ? 2000 : 255 }}
      onChange={(event) => onChange(event.target.value)}
      sx={field.input === "longtext" ? { gridColumn: "1 / -1" } : undefined}
    />
  );
}

/** "+ Something": a small link-style button. */
function AddLink({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <Box>
      <Button size="small" startIcon={<Add />} onClick={onClick} disabled={disabled} sx={{ mt: 0.5, textTransform: "none", fontWeight: 700, px: 0.75 }}>
        {children}
      </Button>
    </Box>
  );
}

function AiButton({ icon, label, children, onClick, disabled }: { icon: ReactNode; label: string; children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <Button
      size="small"
      aria-label={label}
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, px: 1.25, py: 0.25, color: "#6D28D9", bgcolor: "#F5F3FF", border: 1, borderColor: "#DDD6FE", "&:hover": { bgcolor: "#EDE9FE", borderColor: "#7C3AED" } }}
    >
      {children}
    </Button>
  );
}

/** A section's state in the list on the left. */
function SectionDot({ state }: { state: "missing" | "todo" | "done" }) {
  if (state === "done") return <CheckCircle sx={{ fontSize: 17 }} color="success" />;
  if (state === "missing") return <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "warning.light", color: "warning.dark", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800 }}>!</Box>;
  return <RadioButtonUnchecked sx={{ fontSize: 17, color: "text.disabled" }} />;
}

/** "7/9" in a ring that fills as the listing gets ready. */
function ProgressRing({ done, total, color }: { done: number; total: number; color: string }) {
  return (
    <Box sx={{ position: "relative", width: 44, height: 44, flexShrink: 0 }} aria-label={`${done} of ${total} ready`}>
      <CircularProgress variant="determinate" value={100} size={44} thickness={4} sx={{ color: "action.hover", position: "absolute" }} />
      <CircularProgress variant="determinate" value={(done / Math.max(1, total)) * 100} size={44} thickness={4} sx={{ color, position: "absolute" }} />
      <Typography sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 11.5, fontWeight: 800 }}>{done}/{total}</Typography>
    </Box>
  );
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "?";
}

/** One thing left to do, which takes you to where it's fixed. */
function TodoRow({ check, onGo, soft = false }: { check: Check; onGo?: () => void; soft?: boolean }) {
  const content = (
    <>
      {soft ? <RadioButtonUnchecked sx={{ fontSize: 17, color: "text.disabled" }} /> : <WarningAmber sx={{ fontSize: 17 }} color="warning" />}
      <Typography variant="body2" fontWeight={soft ? 500 : 600} color={soft ? "text.secondary" : "text.primary"} sx={{ flex: 1, textAlign: "left", lineHeight: 1.4 }}>
        {check.todo}
      </Typography>
      {onGo && <Typography component="span" variant="caption" fontWeight={700} color="primary.main">{check.target === "edit-venue" ? "Edit venue" : "Go"}</Typography>}
    </>
  );
  const sx = { display: "flex", alignItems: "center", gap: 1, width: "100%", px: 1, py: 0.75, borderRadius: 1.5 };
  return onGo ? (
    <ButtonBase onClick={onGo} sx={{ ...sx, "&:hover": { bgcolor: "action.hover" } }}>{content}</ButtonBase>
  ) : (
    <Box sx={sx}>{content}</Box>
  );
}
